import React, { Component, ErrorInfo, ReactNode } from "react";
import { Mascot } from "./Mascot";
import { Button } from "./ui/button";
import { RefreshCw, Home, Copy } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  componentStack?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Store component stack for optional on-screen debugging.
    // (Safe: only rendered when ?debug=1)
    this.setState({ componentStack: errorInfo.componentStack });
  }

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = "/";
  };

  private isDebugEnabled() {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("debug") === "1";
    } catch {
      return false;
    }
  }

  private handleCopyDebug = async () => {
    const payload = {
      message: this.state.error?.message,
      stack: this.state.error?.stack,
      componentStack: this.state.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    } catch {
      // Fallback: best-effort prompt
      window.prompt("Copy debug info:", JSON.stringify(payload, null, 2));
    }
  };

  public render() {
    if (this.state.hasError) {
      const debug = this.isDebugEnabled();

      return (
        <div className="min-h-dvh bg-background flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <Mascot mood="sad" className="w-32 h-32 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Oops! Something went wrong
            </h1>
            <p className="text-muted-foreground mb-6">
              Don&apos;t worry, it&apos;s not your fault. Let&apos;s get you back on track.
            </p>

            {debug && (
              <div className="mb-6 rounded-lg border bg-card p-3 text-left">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      Debug details
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Copy this and paste it into chat.
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={this.handleCopyDebug}>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                </div>

                <div className="mt-3 space-y-2">
                  <div>
                    <div className="text-xs font-medium text-foreground">Error</div>
                    <pre className="mt-1 max-h-28 overflow-auto rounded bg-muted p-2 text-[11px] leading-snug text-foreground">
                      {this.state.error?.message || "(no message)"}
                    </pre>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-foreground">Component stack</div>
                    <pre className="mt-1 max-h-28 overflow-auto rounded bg-muted p-2 text-[11px] leading-snug text-foreground">
                      {this.state.componentStack || "(no component stack)"}
                    </pre>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <Button onClick={this.handleRefresh} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button onClick={this.handleGoHome}>
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </div>

            {debug && (
              <p className="mt-4 text-xs text-muted-foreground">
                Tip: you can also open <code>/browse?debug=1</code> to capture the crash.
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
